/**
 * Artifact staleness for the event workspace (ADR-0009, completed by #243).
 *
 * "Needs regeneration" decomposes into exactly two facts:
 *
 *   stale = project(stored snapshot) !== project(last fetched read)
 *           OR a contributing section is dirty (has unsaved edits)
 *
 * Both projection operands are produced by the same Go serializers — the
 * generation-time snapshot (`buildWorkspaceStalenessSnapshot`) is built from
 * the same queries the `/details` read uses, pinned by
 * `TestStalenessSnapshotMatchesRead_Integration` — so comparing them requires
 * only picking WHICH fields count. There is no coercion here and no mirror of
 * the Go write handlers: unsaved editor state never enters the comparison.
 * Unsaved edits are covered by the dirty half instead (use-dirty-sections.ts):
 * a gear-editor edit is a quote+job input and a job-details edit is a job
 * input, so `dirty → stale` is exact, not approximate. The dirty flag spans
 * until the post-save refetch lands (each section's autosave `save` awaits
 * `onRefresh`), so the saved change is visible to the projection compare by
 * the time the flag drops — no two-clocks gap (#175).
 *
 * The projections deliberately exclude identity churn: row/assignment ids
 * (a gear PUT is delete+reinsert and a crew save is a full replace, both
 * minting new ids for unchanged content), sort_order, and array order (rows
 * are ordered by content). A field is included iff changing it changes the
 * rendered artifact.
 */
import type {
  WorkspaceEventData,
  WorkspaceStalenessSnapshot,
} from "./event-workspace-api";
import { normalizeBookingItemType } from "./booking-item-types";
import { isActive } from "./assignment-status";

// ─── Stable serialisation ────────────────────────────────────────────────────

/**
 * Deterministic JSON serialisation. Object keys are sorted; arrays keep
 * caller-provided order. null and undefined produce the same output. Also the
 * autosave dirty fingerprint (use-autosave.ts).
 */
export function stableStringify(value: unknown): string {
  if (value === undefined || value === null) return "null";
  if (typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) {
    return `[${value.map(stableStringify).join(",")}]`;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts = keys.map(
    (k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`,
  );
  return `{${parts.join(",")}}`;
}

/** Order rows by their serialised content — identity- and index-free. */
function sortByContent<T>(rows: T[]): T[] {
  return rows
    .map((r) => ({ r, key: stableStringify(r) }))
    .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
    .map(({ r }) => r);
}

/** Blank and absent package keys read as null (legacy snapshots ship ""). */
function packageKeyOrNull(key: string | null | undefined): string | null {
  return key == null || key === "" ? null : key;
}

// ─── Projections ─────────────────────────────────────────────────────────────
// Each projection maps a server payload (the live read or the stored snapshot
// — same shape, WorkspaceEventData ⊇ WorkspaceStalenessSnapshot) to the
// canonical string of the fields that render on that artifact.

/** The quote face: every package's rows, the approval settings, the pricing inputs. */
export function projectQuoteSummary(snap: WorkspaceStalenessSnapshot): string {
  const gear = sortByContent(
    snap.gearItems.map((it) => ({
      item_name: it.item_name,
      quantity: it.quantity,
      starts_on: it.starts_on ?? null,
      ends_on: it.ends_on ?? null,
      hire_rate: it.hire_rate ?? null,
      equipment_id: it.equipment_id ?? null,
      type: it.type ?? null,
      quote_group: it.quote_group,
      discount: it.discount ?? 0,
      notes: it.notes ?? null,
      quote_group_summarised: it.quote_group_summarised ?? false,
      package_key: packageKeyOrNull(it.package_key),
    })),
  );

  const approval =
    snap.approval == null
      ? null
      : {
          deposit_percent: snap.approval.deposit_percent ?? null,
          quote_valid_days: snap.approval.quote_valid_days ?? null,
          quote_notes: snap.approval.quote_notes ?? null,
          discount: snap.approval.discount ?? null,
        };

  return stableStringify({
    gear,
    packages: (snap.quotePackages ?? []).map((p) => ({
      key: p.key,
      name: p.name,
    })),
    approval,
    // Every Event Details field the money documents render. The quote face
    // prints the identity block and the event line (internal/pdf/quote.go
    // renderQuoteHeader), the invoice faces print the same plus the purchase
    // order — and both faces borrow this one projection, so a field that
    // reaches either belongs here. Until #4xx only client_email did, which
    // left an Operator renaming the Organization or moving the event date
    // with a sent quote that silently no longer matched the Booking.
    //
    // purchase_order is the one field written by someone other than the
    // Operator: the Customer types it on the approval page, and the deposit
    // invoice the approval worker sends already carries it. That write patches
    // the stored snapshot in place (SyncSnapshotPurchaseOrder, no
    // quote_summary_generated_at bump), so the Customer's PO stays silent —
    // #294's fix, kept — while an Operator's later hand edit diverges from the
    // snapshot and flags like every other field here.
    organization: snap.booking.organization ?? null,
    client_name: snap.booking.client_name ?? null,
    client_email: snap.booking.client_email ?? null,
    client_phone: snap.booking.client_phone ?? null,
    purchase_order: snap.booking.purchase_order ?? null,
    event_name: snap.booking.event_name ?? null,
    event_date: snap.booking.event_date ?? null,
    event_end_date: snap.booking.event_end_date ?? null,
    event_time: snap.booking.event_time ?? null,
    multi_day_extra_day_percent: snap.multiDayExtraDayPercent ?? null,
  });
}

/**
 * The job face: the operations booking fields, the job-scoped gear (package 1
 * + unpackaged rows; item-typed rows only), the active crew, the day
 * schedules, and the transport block.
 */
export function projectJobSummary(snap: WorkspaceStalenessSnapshot): string {
  const b = snap.booking;

  // Job artifacts are built from package 1's rows on a multi-package booking
  // (rows with no package always count) — scope the projection the same way.
  const packages = snap.quotePackages ?? [];
  const jobScopeKey = packages.length > 0 ? packages[0].key : null;
  const scoped = snap.gearItems.filter((r) => {
    const pk = packageKeyOrNull(r.package_key);
    return jobScopeKey == null || pk == null || pk === jobScopeKey;
  });

  const gear = sortByContent(
    scoped
      .filter((r) => normalizeBookingItemType(r.type) === "item")
      .map((r) => ({
        item_name: r.item_name,
        quantity: r.quantity,
        notes: r.notes ?? null,
      })),
  );

  const assignments = sortByContent(
    snap.assignments.filter(isActive).map((a) => ({
      contractor_id: a.contractorId ?? null,
      hourly_rate: a.hourlyRate ?? null,
      estimated_hours: a.estimatedHours ?? null,
      pay_amount: a.payAmount ?? null,
      tasks_description: a.tasksDescription ?? null,
      jobsheet_template_id: a.jobsheetTemplateId ?? null,
      equipment_assigned: a.equipmentAssigned ?? null,
      // The driver's vehicle prints on their job sheet (migration 120), so
      // changing it makes the job summary stale.
      vehicle_name: a.vehicleName ?? null,
    })),
  );

  const daySchedules = [...snap.daySchedules]
    .sort((x, y) => x.day_number - y.day_number)
    .map((s) => ({
      day_number: s.day_number,
      schedule_date: s.schedule_date ?? null,
      kind: s.kind ?? null,
      site_open: s.site_open ?? null,
      call_time: s.call_time ?? null,
      event_start: s.event_start ?? null,
      event_end: s.event_end ?? null,
      pack_out: s.pack_out ?? null,
      ready_by: s.ready_by ?? null,
      notes: s.notes ?? null,
    }));

  // Transport block: route + planned vehicles + the net charge the job sheet
  // prints (the sum of the scoped logistics rows; null when there are none).
  let transportTotal: number | null = null;
  for (const r of scoped) {
    if (normalizeBookingItemType(r.type) !== "logistics") continue;
    transportTotal =
      (transportTotal ?? 0) + (r.hire_rate ?? 0) - (r.discount ?? 0);
  }
  const transport = {
    route_km: b.transport_route_km ?? null,
    total: transportTotal,
    vehicles: sortByContent(
      (snap.bookingVehicles ?? []).map((v) => ({ name: v.vehicle_name })),
    ),
  };

  return stableStringify({
    booking: {
      event_name: b.event_name ?? null,
      event_date: b.event_date ?? null,
      event_end_date: b.event_end_date ?? null,
      event_time: b.event_time ?? null,
      location: b.location ?? null,
      call_time: b.call_time ?? null,
      pack_out_time: b.pack_out_time ?? null,
      site_available_from: b.site_available_from ?? null,
      call_out_notes: b.call_out_notes ?? null,
      // The Booking-level vehicle_type/vehicle_contractor_id pair is gone from
      // this projection with migration 120: nothing writes it and no job sheet
      // renders it, so a historic value could only ever fire a false stale.
      vehicle_amount: b.vehicle_amount ?? null,
      band_names: b.band_names ?? null,
      quote_number: b.quote_number ?? null,
      client_name: b.client_name ?? null,
      client_email: b.client_email ?? null,
      client_phone: b.client_phone ?? null,
      venue_contact: b.venue_contact ?? null,
      event_type: b.event_type ?? null,
      attendance: b.attendance ?? null,
      indoor_outdoor: b.indoor_outdoor ?? null,
      has_stage: b.has_stage ?? null,
      stage_details: b.stage_details ?? null,
      power_access: b.power_access ?? null,
      needs_generator: b.needs_generator ?? null,
      wet_weather_plan: b.wet_weather_plan ?? null,
      content_requirements: b.content_requirements ?? null,
    },
    gear,
    assignments,
    daySchedules,
    transport,
  });
}

// ─── Staleness booleans ──────────────────────────────────────────────────────

/**
 * Per-section dirty flags published by the mounted editors
 * (use-dirty-sections.ts). A gear-list edit is a quote AND job input; a
 * job-details edit is a job input only.
 */
export interface SectionDirtyFlags {
  gearList?: boolean;
  jobDetails?: boolean;
}

export interface WorkspaceStaleness {
  quoteSummary: boolean;
  jobSummary: boolean;
}

/**
 * An artifact is stale iff it has been generated, carries a stored
 * generation-time snapshot, AND (its projection of the current read differs
 * from its projection of that snapshot, OR a contributing section is dirty).
 * A missing snapshot (never generated, or generated before #175) reads FRESH.
 * Memoise on (data, dirty).
 */
export function computeWorkspaceStaleness(
  data: WorkspaceEventData,
  dirty: SectionDirtyFlags,
): WorkspaceStaleness {
  const gearDirty = dirty.gearList === true;
  const jobDirty = gearDirty || dirty.jobDetails === true;

  const quoteSnapshot = data.quoteSummarySnapshot;
  const quoteSummary =
    data.booking.quote_summary_generated_at != null &&
    quoteSnapshot != null &&
    (gearDirty ||
      projectQuoteSummary(data) !== projectQuoteSummary(quoteSnapshot));

  const jobSnapshot = data.jobSummarySnapshot;
  const jobSummary =
    data.booking.job_summary_generated_at != null &&
    jobSnapshot != null &&
    (jobDirty || projectJobSummary(data) !== projectJobSummary(jobSnapshot));

  return { quoteSummary, jobSummary };
}
